import { useEffect, useState } from "react";
import { Loader2, MessageSquare, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function PreferredChannelBulkDialog({ open, onClose, contactIds, allWithChannel, onDone }) {
  const [value, setValue] = useState("whatsapp");
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const body = { contact_ids: allWithChannel ? [] : contactIds, all_with_channel: allWithChannel || null, value };
  useEffect(() => {
    if (!open) return;
    setPreview(null); setConfirm(false);
    api.post("/contacts/preferred-channel/preview", body).then((r) => setPreview(r.data)).catch((e) => toast.error(formatApiErrorDetail(e, "Anteprima non disponibile")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value, allWithChannel, contactIds.length]);
  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post("/contacts/preferred-channel/bulk", { ...body, confirm: true });
      toast.success(`Canale preferito aggiornato per ${r.data.updated} contatti (${r.data.unchanged} già impostati)`);
      onDone?.(r.data); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e, "Aggiornamento non riuscito")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="preferred-channel-bulk-dialog">
        <DialogHeader><DialogTitle>Imposta canale preferito</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400">Modifica solo la <b>preferenza operativa</b> di contatto. Non tocca consensi, iscrizioni o STOP: un contatto disiscritto resta disiscritto.</p>
        <div className="flex gap-2">
          {[["whatsapp", "WhatsApp", MessageSquare], ["sms", "SMS", Smartphone]].map(([v, l, Icon]) => (
            <button key={v} type="button" onClick={() => setValue(v)} data-testid={`pref-bulk-value-${v}`}
                    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm border transition-colors ${value === v ? (v === "sms" ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-[#10B981] text-white border-[#10B981]") : "border-[#1F2937] text-slate-300 hover:border-slate-500"}`}>
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
        </div>
        <div className="rounded-lg border border-[#1F2937] bg-[#0B1120]/40 p-3 text-sm space-y-1" data-testid="pref-bulk-preview">
          {!preview ? <span className="inline-flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Calcolo anteprima…</span> : (<>
            <p><b data-testid="pref-bulk-total">{preview.total}</b> {preview.total === 1 ? "contatto" : "contatti"} {allWithChannel ? `con canale preferito ${allWithChannel === "sms" ? "SMS" : "WhatsApp"}` : preview.total === 1 ? "selezionato" : "selezionati"}</p>
            <p className="text-[#047857]"><b data-testid="pref-bulk-to-change">{preview.to_change}</b> {preview.to_change === 1 ? "passerà" : "passeranno"} a {value === "sms" ? "SMS" : "WhatsApp"}{preview.from_sms ? ` (${preview.from_sms} da SMS)` : ""}{preview.from_whatsapp ? ` (${preview.from_whatsapp} da WhatsApp)` : ""}</p>
            <p className="text-slate-400">{preview.unchanged} già {preview.unchanged === 1 ? "impostato, invariato" : "impostati, invariati"}</p>
            {preview.unsubscribed > 0 && <p className="text-[#B45309]">{preview.unsubscribed} non {preview.unsubscribed === 1 ? "iscritto" : "iscritti"}: cambia solo la preferenza, {preview.unsubscribed === 1 ? "non riceverà" : "non riceveranno"} comunque campagne.</p>}
          </>)}
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer">
          <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-0.5 accent-[#10B981]" data-testid="pref-bulk-confirm" />
          Confermo: modifico solo la preferenza di canale di {preview?.to_change ?? "…"} {preview?.to_change === 1 ? "contatto" : "contatti"}; nessun consenso viene creato o modificato. L'operazione è registrata nel Log attività e nella cronologia di ogni contatto.
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="pref-bulk-cancel">Annulla</Button>
          <Button onClick={apply} disabled={!confirm || busy || !preview || !preview.to_change} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="pref-bulk-apply">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Applica a {preview?.to_change ?? 0} {preview?.to_change === 1 ? "contatto" : "contatti"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
